import {
  MAX_ORGANIZE_BY_LEVELS_PER_COLUMN_GROUP,
  TOTALS_COLUMN_LABEL,
  TOTALS_COLUMN_VALUE_KEY
} from '../../constants';
import type { ColumnValue, ColumnValueSegment } from '../base';
import { buildColumnPathKey } from './columnPathKey';

/**
 * Column-values tuple layout, shared by the service that emits it and the app that reads it.
 *
 * Slot 0 is the column group id; each organize-by level then contributes an id slot and a label
 * slot. Level 1 keeps slots 1 and 2, so a one-level column group produces exactly the three-element
 * tuple it did before multi-level support.
 */
export const idSlot = (level: number): number => 2 * level - 1;
export const labelSlot = (level: number): number => 2 * level;

/** Slot count across all levels, including slot 0. */
export const TOTAL_COLUMN_VALUE_SLOTS = 1 + 2 * MAX_ORGANIZE_BY_LEVELS_PER_COLUMN_GROUP;

/**
 * How many organize-by levels a tuple carries.
 *
 * A level counts as present when EITHER of its slots is non-null. Unused levels are NULL-padded in
 * both slots, so they still read as absent; the per-column-group totals sentinel carries a null id
 * with a 'Totals' label, so it still reads as a real level-1 entry; and a level whose display name
 * is NULL — `child_group_name` and the account-string column are projected unfiltered, so this does
 * occur — still counts, instead of truncating the path and collapsing the column onto its parent.
 * A legacy three-element tuple simply runs out of slots after level 1.
 *
 * Returns the DEEPEST present level rather than stopping at the first absent one. Padding is only
 * ever trailing, so for every tuple the SQL emits the two agree — but a hole (both slots null at an
 * intermediate level, reachable when a segment projects a null id AND a null name) would otherwise
 * truncate the path and hand the column its own ancestor's key, which silently merges the two in the
 * header tree and the pivot. Reading through the hole keeps the column's depth, and therefore its
 * identity, distinct from its ancestor's.
 */
export const columnValueLevelCount = (tuple: ColumnValue): number => {
  let count = 0;
  for (let level = 1; level <= MAX_ORGANIZE_BY_LEVELS_PER_COLUMN_GROUP; level++) {
    const isPresent = !isNullish(tuple[idSlot(level)]) || !isNullish(tuple[labelSlot(level)]);
    if (isPresent) count = level;
  }
  return count;
};

const isNullish = (segment: ColumnValueSegment | undefined): boolean => segment === null || segment === undefined;

/** The tuple's level ids, outermost first — the raw form of its path. */
export const extractColumnValueIds = (tuple: ColumnValue): ColumnValueSegment[] => {
  const ids: ColumnValueSegment[] = [];
  const levels = columnValueLevelCount(tuple);
  for (let level = 1; level <= levels; level++) {
    ids.push(tuple[idSlot(level)]);
  }
  return ids;
};

/**
 * Separator between organize-by levels wherever a column's path is flattened into one string.
 *
 * A chevron rather than a dash on purpose: a column group name can itself contain a dash — `General
 * Ledger Balances - V4` is a real one — so joining on a dash leaves no way to see where the name
 * ends and the path begins.
 *
 * Surfaces that can show the levels structurally (the grid's nested header rows) should keep doing
 * that; this is for the ones with a single line to work with — the formula picker, the chart series
 * picker, and the details grid header.
 */
export const COLUMN_LEVEL_SEPARATOR = ' › ';

/**
 * The tuple's level labels, outermost first, each falling back to that level's raw id.
 *
 * The symmetric counterpart of {@link extractColumnValueIds}: callers that need to DISPLAY a path
 * use this, callers that need to IDENTIFY one use the ids. Deriving both from the same level count
 * keeps a label from being read off a different depth than the key it belongs to.
 */
export const extractColumnValueLabels = (tuple: ColumnValue): string[] => {
  const labels: string[] = [];
  const levels = columnValueLevelCount(tuple);
  for (let level = 1; level <= levels; level++) {
    const label = tuple[labelSlot(level)];
    const id = tuple[idSlot(level)];
    labels.push(String(label ?? id ?? ''));
  }
  return labels;
};

/**
 * A column's whole organize-by path as one display string, outermost first.
 *
 * Every level is included whenever the column has more than one, because showing only some of them
 * leaves two columns under different ancestors reading identically. A one-level column returns its
 * bare label, so single-level reports are unchanged.
 */
export const buildColumnPathLabel = (tuple: ColumnValue): string =>
  extractColumnValueLabels(tuple).join(COLUMN_LEVEL_SEPARATOR);

/**
 * The AG Grid field key for a column value: `columnGroupId.levelPath.`
 *
 * This is the column's identity everywhere a field key is the lookup — `hiddenColumns`, saved column
 * order and width (AG Grid derives colId from field), and the calculated-column reference hover. It
 * lives here so the table that emits the field and everything that matches against it cannot drift.
 *
 * A totals column has no id of its own, so it takes the caller's placeholder — the same substitution
 * the table has always made. `emptyValuePlaceholder` differs by caller (a locale string in the app,
 * a constant in the service), so it is passed in rather than assumed.
 */
export const buildColumnFieldKey = (tuple: ColumnValue, emptyValuePlaceholder: string): string => {
  const pathKey = isTotalsColumnValue(tuple)
    ? emptyValuePlaceholder
    : buildColumnPathKey(extractColumnValueIds(tuple));
  return `${String(tuple[0])}.${pathKey || emptyValuePlaceholder}.`;
};

/**
 * Whether two column-value tuples name the same column.
 *
 * Compares identity — column group id plus level path — rather than the tuples' contents. The
 * pickers used `option.join() === value?.join()`, which fails on two counts: a tuple read from
 * `/next/pivot/columns` can be NULL-padded wider than the one persisted in the report config, and
 * `join()` uses a comma, which a text column value can itself contain. Labels are excluded on
 * purpose — a renamed column is still the same column.
 */
export const columnValuesMatch = (a: ColumnValue | undefined, b: ColumnValue | undefined): boolean => {
  if (!a || !b) return false;
  if (String(a[0]) !== String(b[0])) return false;
  // Depth first: a whole-column-group wildcard is `[columnGroupId]` and carries no levels, while the
  // Totals sentinel carries one whose id is null. Both encode to the same path key, so comparing
  // only the key would let a wildcard select Totals.
  if (columnValueLevelCount(a) !== columnValueLevelCount(b)) return false;
  return buildColumnPathKey(extractColumnValueIds(a)) === buildColumnPathKey(extractColumnValueIds(b));
};

/**
 * Whether a tuple is a column group's per-CG Totals sentinel rather than a real data column.
 *
 * The one predicate for this. Three hand-rolled variants used to coexist — `id == null` in the
 * details flattener, `id === null || id === 'null'` in the field key, and a `pathKey === 'null'`
 * comparison in the table — and because {@link buildColumnPathKey} encodes a null id as the string
 * `'null'`, which is also `TOTALS_COLUMN_VALUE_KEY`, they disagreed about any column whose level-1
 * id is null.
 *
 * The sentinel is recognised by its SHAPE, not by its id alone: the columns pipeline writes exactly
 * `[columnGroupId, null, TOTALS_COLUMN_LABEL]`, so it is a one-level tuple with no id. A real
 * column that has no segment id but does carry a name — nothing filters a null `child_group_id` out
 * of either query pipeline, and adding such a filter to one of them alone would stop the leaf
 * columns summing to the group total — keeps its own identity instead of being read as Totals.
 *
 * A tuple with neither an id nor a name at any level is still read as Totals; that case is
 * indistinguishable from the sentinel in the response, and reading it as a data column would give
 * it an empty path key.
 */
export const isTotalsColumnValue = (tuple: ColumnValue): boolean => {
  const pathIds = extractColumnValueIds(tuple);
  if (pathIds.length === 0) return true;
  const hasRealId = pathIds.some((id) => id !== null && id !== undefined && id !== TOTALS_COLUMN_VALUE_KEY);
  if (hasRealId) return false;
  // No id anywhere. A level-1 label other than 'Totals' means this is a real column whose segment
  // id is null, not the sentinel — the two are otherwise identical.
  const label = tuple[labelSlot(1)];
  return isNullish(label) || String(label) === TOTALS_COLUMN_LABEL;
};
